import React from 'react';
import { useParams, Link } from 'react-router-dom';
import TriEventsLoadingScreen from '@/components/common/TriEventsLoadingScreen';
import BackButton from '@/components/common/BackButton';

// Shadcn Components
import { Card, CardContent, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Calendar, Plus } from "lucide-react";

// Hooks
import { useTenantNavigation } from '../../hooks/navigation/useTenantNavigation';
import { useEventsOnDay } from '@/hooks/event/useEventsOnDay';

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) {
    return 'Invalid date';
  }
  // Just get the date part (YYYY-MM-DD) and format it nicely
  const datePart = dateStr.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  return `${monthNames[month - 1]} ${day}, ${year}`;
}

function EventsOnDay(): React.ReactElement {
  const { date } = useParams<{ date: string }>();
  const { getTenantPath } = useTenantNavigation();
  const tenantId = localStorage.getItem('tenantId');
  const { events, loading, error, eventTicketsAvailable } = useEventsOnDay(date, tenantId);

  if (loading) {
    return <TriEventsLoadingScreen loading={true} />;
  }

  if (error) {
    return (
      <section className="min-h-screen bg-background">
        <div className="container mx-auto">
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-background">
      <div className="container mx-auto pb-12">
        <div>
          <div className="mb-0 md:mb-6">
            <BackButton to={getTenantPath('/dashboard')} text="Back to Dashboard" />
          </div>
          
          <Card className="border-none bg-[#23d6e4] text-white">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <CardTitle className="text-3xl font-bold mb-2 text-white">
                    Events on {formatDate(date)}
                  </CardTitle>
                  <p className="text-white/80">Browse and manage events scheduled for this day</p>
                </div>
                <Button
                  asChild
                  size="lg"
                  className="w-full md:w-auto gap-3 text-lg transition-transform duration-200 hover:scale-105"
                >
                  <Link to={getTenantPath('/events/create')}>
                    <Plus className="h-6 w-6 stroke-[3.5]" />
                    Create Event
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          {events.length > 0 ? (
            <div className="mt-6 space-y-6">
              <h2 className="text-xl font-semibold text-foreground">
                {events.length === 1 ? '1 Event Found' : `${events.length} Events Found`}
              </h2>
              <div className="grid gap-6 sm:grid-cols-1 lg:grid-cols-2">
                {events.map(event => (
                  <Card 
                    key={event.uuid} 
                    className="group overflow-hidden transition-all duration-300 hover:shadow-lg"
                  >
                    {event.image && (
                      <div className="relative h-48 w-full">
                        <img
                          src={event.image}
                          alt={event.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <CardContent className="p-6">
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <h3 className="text-2xl font-semibold tracking-tight truncate">
                            {event.title}
                          </h3>
                          <p className="text-muted-foreground truncate">
                            {event.description}
                          </p>
                        </div>
                        
                        <div className="space-y-2 text-sm">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-muted-foreground shrink-0" />
                            <span className="text-muted-foreground truncate">
                              {formatDate(event.date)}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground shrink-0">Time:</span>
                            <span className="truncate">{event.time_start} - {event.time_end}</span>
                          </div>
                          {event.location && (
                            <div className="flex items-center gap-2">
                              <span className="text-muted-foreground shrink-0">Location:</span>
                              <span className="truncate">{event.location}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground shrink-0">Contact:</span>
                            <span className="truncate">{event.contact_email}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground shrink-0">Tickets Available:</span>
                            <Badge variant="secondary" className="shrink-0">
                              {eventTicketsAvailable[event.uuid] || 0}
                            </Badge>
                          </div>
                        </div>

                        <Button
                          asChild
                          className="w-full mt-4 bg-[#23d6e4] text-white transition-transform duration-200 hover:scale-105"
                        >
                          <Link to={getTenantPath(`/event/${event.uuid}`)}>
                            View Event
                          </Link>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ) : (
            <Card className="border-dashed">
              <CardContent className="p-8 text-center">
                <Calendar className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-semibold text-foreground mb-2">
                  No events on this day
                </h3>
                <p className="text-muted-foreground">
                  Check another date or create a new event
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </section>
  );
}

export default EventsOnDay;
